// CanvasView マウント時に RealtimeClient を作成し、サーバからの op を doc store へ反映する。
// authMode === "mock" のときは mock realtime（自分が送った op をエコーバック）を使い、
// それ以外では WebSocket 経由で実サーバに接続する。

import { useEffect, useMemo, useState } from "react";
import { useShowToast } from "@meiden-connect/ui";
import { getWebConfig } from "../env";
import { createMockRealtimeClient } from "./mock-realtime";
import { createWebSocketRealtimeClient } from "./websocket-realtime";
import { useCanvasDocStore } from "../features/canvas/store/canvas-doc-store";
import { useCanvasViewersStore } from "../features/canvas/store/canvas-viewers-store";
import { useCanvasTransientStore } from "../features/canvas/store/canvas-transient-store";
import type { RealtimeClient } from "./realtime-client";
import type { CanvasObject, CanvasOp, ServerMessage } from "@refinement-canvas/domain";
import { useCanvasApi } from "../lib/canvas-api";

interface State {
  readonly client: RealtimeClient | null;
  readonly status: "idle" | "connecting" | "connected" | "error";
  readonly error?: Error | undefined;
}

export function useRealtimeConnection(canvasId: string): State {
  const config = getWebConfig();
  const canvasApi = useCanvasApi();
  const onServerCommit = useCanvasDocStore((s) => s.onServerCommit);
  const onServerReject = useCanvasDocStore((s) => s.onServerReject);
  const loadSnapshot = useCanvasDocStore((s) => s.loadSnapshot);
  const setViewers = useCanvasViewersStore((s) => s.setViewers);
  const setCursor = useCanvasViewersStore((s) => s.setCursor);
  const setViewerView = useCanvasViewersStore((s) => s.setView);
  const resetViewers = useCanvasViewersStore((s) => s.reset);
  const showToast = useShowToast();

  // クライアント自身は再生成したくないので useMemo + キーで安定させる
  const client = useMemo<RealtimeClient>(() => {
    if (config.authMode === "mock") {
      return createMockRealtimeClient();
    }
    return createWebSocketRealtimeClient({ websocketUrl: config.realtimeWebSocketUrl });
  }, [config.authMode, config.realtimeWebSocketUrl]);

  const [status, setStatus] = useState<State["status"]>("idle");
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let cancelled = false;
    setStatus("connecting");

    // サーバからの message を doc store / viewers store に橋渡しする
    const unsubscribe = client.onMessage((message: ServerMessage) => {
      switch (message.type) {
        case "op.commit":
          onServerCommit(message.op, message.serverSeq);
          // 確定 op が届いたら、対応する操作中ゴースト（一時プレビュー）を破棄して
          // 確定済みオブジェクトとゴーストが二重表示されるのを防ぐ。
          clearTransientForCommittedOp(message.op);
          return;
        case "op.reject":
          onServerReject(message.clientOpId);
          showToast(
            "warning",
            message.reason || "操作を保存できませんでした。必要に応じて新しいキャンバスへ分割してください。"
          );
          // 楽観更新済みの表示をサーバ権威状態へ戻すため、拒否時は最新スナップショットを取得する。
          void (async () => {
            try {
              const data = await canvasApi.fetchCanvasBootstrap(canvasId);
              if (cancelled) return;
              if (data.canvasId !== canvasId) return;
              loadSnapshot({
                canvasId: data.canvasId,
                serverSeq: data.serverSeq,
                objects: [...data.objects],
                meta: data.meta,
              });
            } catch (e) {
              if (cancelled) return;
              console.warn("[realtime] op.reject 後の bootstrap に失敗", e);
            }
          })();
          return;
        case "bootstrap.snapshot":
          loadSnapshot({
            canvasId,
            serverSeq: message.serverSeq,
            objects: message.objects as CanvasObject[],
          });
          return;
        case "viewer.snapshot":
          setViewers(message.viewers);
          return;
        case "viewer.cursor":
          if (message.cursor) {
            setCursor(message.userId, {
              xRatio: message.cursor.xRatio,
              yRatio: message.cursor.yRatio,
              updatedAt: Date.now(),
            });
          } else {
            setCursor(message.userId, null);
          }
          return;
        case "viewer.view":
          // フォーカスモード用の他者ビュー更新。永続化しない情報なので最新値だけを保持する。
          if (message.view) {
            setViewerView(message.userId, {
              panX: message.view.panX,
              panY: message.view.panY,
              zoom: message.view.zoom,
              updatedAt: Date.now(),
            });
          } else {
            setViewerView(message.userId, null);
          }
          return;
        case "op.transient": {
          // 他参加者の操作中プレビュー（ゴースト）。永続化せず transient store に反映し、
          // canvas-remote-previews-layer がリアルタイム描画する。
          const transient = useCanvasTransientStore.getState();
          if (message.op.type === "object.transient.transform") {
            transient.applyRemoteTransform(message.fromUserId, message.op);
          } else if (message.op.type === "pen.stroke.append") {
            transient.appendRemotePen(message.fromUserId, message.op);
          }
          // object.text.cursor は本レイヤでは扱わない（テキスト編集カーソルは別経路）。
          return;
        }
        case "resync.required":
          // サーバ再起動 / シーケンス欠落時は REST bootstrap で最新スナップショットを取り直す。
          // 画面遷移や canvas 切替が走った後にレスポンスが返るケースを考慮し、
          // useEffect の cleanup で立てた cancelled フラグと、レスポンスの canvasId が
          // 現在表示中の canvasId と一致しているかを二重確認してから store を上書きする。
          console.warn("[realtime] resync.required:", message.reason);
          void (async () => {
            try {
              const data = await canvasApi.fetchCanvasBootstrap(canvasId);
              if (cancelled) return;
              if (data.canvasId !== canvasId) return;
              loadSnapshot({
                canvasId: data.canvasId,
                serverSeq: data.serverSeq,
                objects: [...data.objects],
                meta: data.meta,
              });
            } catch (e) {
              if (cancelled) return;
              console.warn("[realtime] resync 後の bootstrap に失敗", e);
            }
          })();
          return;
      }
    });

    void (async () => {
      try {
        // required 環境ではチケットを発行し、WebSocket の ?ticket= に付与する。
        // チケット発行に失敗した場合は接続できないため、エラー扱いとする。
        let ticket: string | undefined;
        if (config.authMode !== "mock") {
          const session = await canvasApi.issueRealtimeSession(canvasId);
          ticket = session.token;
        }
        await client.connect(canvasId, ticket);
        if (cancelled) return;
        setStatus("connected");
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
      client.disconnect();
      resetViewers();
      useCanvasTransientStore.getState().reset();
    };
  }, [
    canvasApi,
    canvasId,
    client,
    config.authMode,
    loadSnapshot,
    onServerCommit,
    onServerReject,
    setViewers,
    setCursor,
    resetViewers,
    showToast,
  ]);

  return { client, status, error };
}

// 確定 op に対応する一時プレビュー（ゴースト）を破棄する。
// - transform: 確定させた本人のゴーストのみ破棄（別ユーザーが同じオブジェクトを操作中の
//   新しいゴーストを誤って消さない）
// - delete: オブジェクト自体が消えるため、誰のゴーストでも破棄
// - create: 採番済みオブジェクトの id でゴースト枠を破棄
// - pen.stroke.commit: 確定ストロークの id（=描画中 strokeId）のゴーストのみ破棄
function clearTransientForCommittedOp(op: CanvasOp): void {
  const transient = useCanvasTransientStore.getState();
  switch (op.type) {
    case "object.transform":
      transient.clearTransformsForObject(op.objectId, op.actorUserId);
      return;
    case "object.delete":
      transient.clearTransformsForObject(op.objectId);
      return;
    case "object.create":
      transient.clearTransformsForObject(op.object.id);
      return;
    case "pen.stroke.commit":
      // 確定オブジェクト ID は描画中 strokeId と一致するため、該当ストロークだけを破棄する。
      transient.clearPenStroke(op.object.id);
      return;
    default:
      return;
  }
}
